<script setup lang="ts">
	import { useDebounceFn } from '@vueuse/core'
	import type { ClassNameValue } from 'tailwind-merge'
	import type { lineTabContext } from '@/types'

	// Interfaces
	interface Body {
		default: string
		alignItem: string
		gap: string
		border: string
		margin: string
		position: string
		overflowX: string
		scroll: string
		padding: string
	}

	interface Active {
		default: string
		color: string
		font: string
	}

	interface Line {
		default: string
		position: string
		leading: string
		border: string
		zIndex: string
		transition: string
		borderColor: string
		height: string
	}

	interface Wrapper {
		default: string
		display: string
		gap: string
	}

	interface Tab {
		wrapper: Partial<Wrapper>
		body: Partial<Body>
		width: ClassNameValue
		default: ClassNameValue
		transition: ClassNameValue
		padding: ClassNameValue
		height: ClassNameValue
		alignItems: ClassNameValue
		display: ClassNameValue
		capitalize: ClassNameValue
		outline: ClassNameValue
		zIndex: ClassNameValue
		active: Partial<Active>
		inactive: Partial<Active>
	}

	interface Root {
		tab: Partial<Tab>
		line: Partial<Line>
	}

	// Props
	const prop = withDefaults(
		defineProps<{
			tabs: lineTabContext[]
			useSearch?: boolean
			searchPlaceholder?: string
			activeTab?: string
			btnClass?: ClassNameValue
			btnActiveClass?: ClassNameValue
			tabBodyClass?: ClassNameValue
			borderLineClass?: ClassNameValue
			tabClass?: ClassNameValue
			useUrlUpdate?: boolean
			btnWrapperClass?: ClassNameValue
			inputSearchClass?: ClassNameValue
			queryKey?: string
			ui?: Partial<Root>
		}>(),
		{
			tabBodyClass: 'mt-5',
			queryKey: 'tab'
		}
	)

	// Default UI Configuration
	const { line, tab }: Root = {
		tab: {
			wrapper: {
				default: 'flex-wrap-reverse',
				display: 'flex items-center justify-between',
				gap: 'gap-y-6'
			},
			body: {
				default: 'tabs-panel whitespace-nowrap flex',
				alignItem: 'items-center',
				gap: 'gap-3',
				border: 'md:border-b',
				margin: 'md:mt-5',
				padding: 'pb-3 lg:pb-0',
				position: 'relative',
				overflowX: 'overflow-x-auto',
				scroll: ''
			},
			width: '',
			default: 'flex',
			transition: 'transition-colors ease-out',
			padding: 'px-3 first:pl-0 md:py-7 py-2',
			height: 'h-[30px]',
			alignItems: 'items-center justify-center',
			display: '',
			capitalize: 'capitalize',
			outline: 'outline-primary-100',
			zIndex: 'z-10',
			active: {
				default: 'font-medium',
				color: 'text-primary-2'
			},
			inactive: { default: '', color: 'text-gray-400', font: 'font-400' }
		},
		line: {
			default: '',
			position: 'absolute bottom-0 right-0',
			leading: 'leading-[30px]',
			border: 'border-b-[2px]',
			zIndex: 'z-10',
			transition: 'transition-all ease-linear',
			borderColor: 'border-primary-2',
			height: 'h-[30px]'
		}
	}

	// Emits
	const emit = defineEmits<{
		(arg: 'selected', param: string): void
		(arg: 'searchField', param: string): void
		(arg: 'update:activeTab', param: string | number): void
	}>()

	// Refs and Computed
	const lineElement = useTemplateRef('lineElement')
	const tabButton = useTemplateRef<HTMLDivElement[] | []>('tabButton')
	const selectedTab = ref<string | number>('')
	const borderProperties = ref({ width: 0, x: 0 })
	const route = useRoute()
	const tabList = computed(() => prop.tabs.filter(item => !item.disabled))
	const { replace } = useRouter()

	// Functions
	const pickTab = async (arg?: string | number) => {
		if (!arg) return

		const dataIndex = tabList.value.findIndex(item => item.key === arg)

		if (prop.useUrlUpdate) {
			const { urlQuery } = useUtils()
			try {
				await replace({
					query: {
						...urlQuery.value,
						[prop.queryKey]: arg
					}
				})
			} catch (error) {
				console.error(error)
			}
		}

		if (lineElement.value) {
			const btnElement = tabButton.value?.[dataIndex] as HTMLDivElement | null
			if (btnElement) {
				emit('selected', String(arg))
				borderProperties.value = {
					width: btnElement.offsetWidth,
					x: btnElement.offsetLeft
				}
			}
		}

		selectedTab.value = String(arg)
		emit('update:activeTab', arg)
	}

	const onActivate = useDebounceFn(pickTab, 100)

	const setTabKey = () => {
		const key =
			route.query?.[prop.queryKey]?.toString() ||
			prop.activeTab ||
			tabList.value.find(item => item.active)?.key ||
			tabList.value[0]?.key ||
			''
		onActivate(key)
	}

	const activateTab = () => {
		if (prop.tabs.length) setTabKey()
	}

	// Watchers
	watch(() => prop.tabs, activateTab, { flush: 'post', immediate: true })
	watch(
		() => prop.activeTab,
		(newVal, oldVal) => {
			if (newVal !== oldVal) {
				pickTab(newVal)
			}
		}
	)
</script>

<template>
	<div class="relative">
		<div class="tabs">
			<div
				:class="
					cn(
						ui?.tab?.wrapper?.default ?? tab?.wrapper?.default,
						ui?.tab?.wrapper?.display ?? tab?.wrapper?.display,
						ui?.tab?.wrapper?.gap ?? tab?.wrapper?.gap
					)
				"
			>
				<div
					ref="tabPanel"
					:class="
						cn(
							ui?.tab?.body?.default ?? tab?.body?.default,
							ui?.tab?.body?.alignItem ?? tab?.body?.alignItem,
							ui?.tab?.body?.border ?? tab?.body?.border,
							ui?.tab?.body?.gap ?? tab?.body?.gap,
							ui?.tab?.body?.overflowX ?? tab?.body?.overflowX,
							ui?.tab?.body?.padding ?? tab?.body?.padding,
							ui?.tab?.body?.position ?? tab?.body?.position,
							ui?.tab?.body?.scroll ?? tab?.body?.scroll
						)
					"
				>
					<button
						v-for="(item, index) in tabList"
						:key="index"
						ref="tabButton"
						data-id="tab-btn"
						:data-testid="`tab-${item.name}`"
						:class="
							cn([
								item.key === selectedTab
									? [
											ui?.tab?.active?.default ?? tab.active?.default,
											ui?.tab?.active?.color ?? tab.active?.color,
											ui?.tab?.active?.font ?? tab.active?.font
										]
									: [
											ui?.tab?.inactive?.color ?? tab.inactive?.color,
											ui?.tab?.inactive?.default ?? tab.inactive?.default,
											ui?.tab?.inactive?.font ?? tab.inactive?.font
										],
								item?.disabled ? 'hidden' : 'flex',
								ui?.tab?.width ?? tab.width,
								ui?.tab?.default ?? tab.default,
								ui?.tab?.alignItems ?? tab.alignItems,
								ui?.tab?.capitalize ?? tab.capitalize,
								ui?.tab?.display ?? tab.display,
								ui?.tab?.height ?? tab.height,
								ui?.tab?.outline ?? tab.outline,
								ui?.tab?.padding ?? tab.padding,
								ui?.tab?.transition ?? tab.transition,
								ui?.tab?.zIndex ?? tab.zIndex
							])
						"
						type="button"
						@click="pickTab(item.key)"
					>
						<slot name="tab" :item="item" :active="selectedTab === item.key">
							<slot :name="`key(${item.key})`" :content="item.name">
								{{ item.name }}
							</slot>
						</slot>
					</button>

					<div
						ref="lineElement"
						:style="{ width: `${borderProperties.width}px`, left: `${borderProperties.x}px` }"
						:class="
							cn(
								borderLineClass,
								ui?.line?.border ?? line.border,
								ui?.line?.borderColor ?? line.borderColor,
								ui?.line?.default ?? line.default,
								ui?.line?.height ?? line.height,
								ui?.line?.leading ?? line.leading,
								ui?.line?.position ?? line.position,
								ui?.line?.transition ?? line.transition,
								ui?.line?.zIndex ?? line.zIndex
							)
						"
					/>
				</div>

				<slot name="head-section">
					<forms-search-field
						v-if="useSearch"
						class="ml-auto h-[50px] w-full bg-white sm:max-w-[380px]"
						:class="cn(inputSearchClass)"
						:placeholder="searchPlaceholder"
						@change="e => emit('searchField', e)"
					/>
				</slot>
			</div>

			<slot name="header" />

			<div class="tab-body relative w-full transition-all" :class="cn(tabBodyClass)">
				<slot name="tag" :is-first="selectedTab === tabList?.[0]?.key" :current="selectedTab" />
				<slot name="override">
					<slot v-if="!$slots?.selected" :name="selectedTab" />
					<slot :selected="selectedTab" />
				</slot>
			</div>
		</div>
	</div>
</template>
